/**
 * IcalCalendarSection — full Dashboard widget that shows:
 *   1. "Oggi/domani" banner (today/tomorrow check-in & check-out, urgent badges)
 *   2. Week view (next 7 days, grouped by date) with one card per booking
 * Each booking card shows: platform tag, dates, property, guest hint,
 * and a CTA "Crea contratto" if `has_contract === false`.
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  CalendarBlank,
  ArrowsClockwise,
  ArrowRight,
  Bed,
  House,
  WarningCircle,
  CheckCircle,
  X,
  Plus,
  FileText,
  Receipt,
  Calculator,
  Trash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_STYLE = {
  airbnb: { label: 'Airbnb', cls: 'bg-rose-500 text-white' },
  booking: { label: 'Booking', cls: 'bg-blue-600 text-white' },
  vrbo: { label: 'Vrbo', cls: 'bg-amber-500 text-white' },
  direct: { label: 'Diretta', cls: 'bg-zinc-800 text-white' },
};

const fmtRelative = (iso, todayIso) => {
  if (iso === todayIso) return 'OGGI';
  try {
    const d = new Date(iso + 'T00:00:00');
    const today = new Date(todayIso + 'T00:00:00');
    const diff = Math.round((d - today) / 86400000);
    if (diff === 1) return 'DOMANI';
    if (diff > 1 && diff < 7) return `TRA ${diff}GG`;
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', weekday: 'short' });
  } catch {
    return iso;
  }
};

const fmtDayOfWeek = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase();
  } catch {
    return iso;
  }
};

export default function IcalCalendarSection() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [hasCalendars, setHasCalendars] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(null); // booking object while modal open
  const [quickAmount, setQuickAmount] = useState('');
  const [quickCityTax, setQuickCityTax] = useState('0');
  const [quickTaxRate, setQuickTaxRate] = useState(21); // 21 or 26 (only for manual/gross)
  const [quickSaving, setQuickSaving] = useState(false);
  const [upcomingLimit, setUpcomingLimit] = useState(10);
  // Default-collapsed: su mobile la lista può essere molto lunga e schiaccia
  // le card "Plugin" sotto. L'utente apre solo quando vuole vedere oltre Domani.
  const [expanded, setExpanded] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualDraft, setManualDraft] = useState({ property_id: '', check_in: '', check_out: '', guest_name: '', num_guests: 2, notes: '' });
  const [savingManual, setSavingManual] = useState(false);
  const [propertiesList, setPropertiesList] = useState([]);
  // Rename dialog (per assegnare manualmente nome ospite)
  const [renameTarget, setRenameTarget] = useState(null); // booking
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  // Confirm delete dialog (blacklist permanente)
  const [confirmDelete, setConfirmDelete] = useState(null); // booking

  const fetchProperties = async () => {
    try {
      const r = await axios.get(`${API}/properties`, { withCredentials: true });
      setPropertiesList(r.data || []);
    } catch {}
  };

  const submitManual = async () => {
    if (!manualDraft.check_in || !manualDraft.check_out) {
      toast.error('Inserisci check-in e check-out');
      return;
    }
    if (manualDraft.check_in >= manualDraft.check_out) {
      toast.error('Il check-out deve seguire il check-in');
      return;
    }
    setSavingManual(true);
    try {
      await axios.post(`${API}/manual-bookings`, manualDraft, { withCredentials: true });
      toast.success('Prenotazione manuale aggiunta');
      setManualOpen(false);
      setManualDraft({ property_id: '', check_in: '', check_out: '', guest_name: '', num_guests: 2, notes: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSavingManual(false);
    }
  };

  const deleteManual = async (id) => {
    if (!window.confirm('Eliminare questa prenotazione manuale?')) return;
    try {
      await axios.delete(`${API}/manual-bookings/${id}`, { withCredentials: true });
      toast.success('Eliminata');
      fetchData();
    } catch {
      toast.error('Errore');
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/ical/calendar?days_past=0&days_ahead=180`, { withCredentials: true });
      setBookings(res.data.bookings || []);
      setToday(res.data.today);
      setHasCalendars(!!res.data.has_configured_calendars);
    } catch {
      // If 0 properties or no urls, just show empty state silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchProperties();
  }, []);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/ical/sync-all`, {}, { withCredentials: true });
      const r = res.data;
      if (r.synced_properties === 0) {
        toast.info('Nessun calendario configurato. Aggiungi un link iCal nelle tue proprietà.');
      } else {
        const parts = [`${r.imported} nuove`, `${r.updated} aggiornate`];
        if (r.removed > 0) parts.push(`${r.removed} cancellate dalla OTA`);
        toast.success(`Sync completata · ${parts.join(' · ')}`);
      }
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleDismiss = (booking) => {
    // Apre conferma — l'eliminazione è permanente (blacklist)
    setConfirmDelete(booking);
  };

  const confirmDismiss = async () => {
    if (!confirmDelete) return;
    const b = confirmDelete;
    setConfirmDelete(null);
    try {
      if (b.source === 'manual') {
        // Le manuali non hanno blacklist, eliminazione semplice
        await axios.delete(`${API}/manual-bookings/${b.id}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/ical/booking/${b.id}/dismiss`, {}, { withCredentials: true });
      }
      toast.success('Prenotazione eliminata · non verrà più reimportata');
      await fetchData();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const openRename = (booking) => {
    setRenameTarget(booking);
    setRenameValue(booking.guest_name || '');
  };

  const saveRename = async () => {
    if (!renameTarget) return;
    const name = renameValue.trim();
    setRenameSaving(true);
    try {
      if (renameTarget.source === 'manual') {
        // Per le manuali aggiorniamo il documento manuale
        await axios.patch(`${API}/manual-bookings/${renameTarget.id}`, { guest_name: name }, { withCredentials: true })
          .catch(async () => {
            // Endpoint potrebbe non esistere — fallback su delete+recreate non sensato.
            // Usiamo invece il delete+recreate? No, lasciamo errore.
            throw new Error('manual_rename_unsupported');
          });
      } else {
        await axios.patch(`${API}/ical/booking/${renameTarget.id}`, { guest_name: name }, { withCredentials: true });
      }
      toast.success(name ? `Ospite impostato: ${name}` : 'Nome ospite rimosso');
      setRenameTarget(null);
      setRenameValue('');
      await fetchData();
    } catch (e) {
      if (e.message === 'manual_rename_unsupported') {
        toast.error('Modifica nome non disponibile per prenotazioni manuali');
      } else {
        toast.error('Errore salvataggio');
      }
    } finally {
      setRenameSaving(false);
    }
  };

  const handleCreateContract = (b) => {
    // Pre-fill the new contract page via query params
    const params = new URLSearchParams({
      check_in: b.check_in,
      check_out: b.check_out,
      property_id: b.property_id || '',
      ical_booking_id: b.id,
    });
    if (b.guest_name) params.set('guest_name', b.guest_name);
    navigate(`/new-contract?${params.toString()}`);
  };

  const handleCreateReceipt = (b) => {
    const params = new URLSearchParams({
      check_in: b.check_in,
      check_out: b.check_out,
      property_id: b.property_id || '',
    });
    if (b.guest_name) params.set('guest_name', b.guest_name);
    navigate(`/receipts?${params.toString()}`);
  };

  const openQuickAdd = (booking, isEdit = false) => {
    setQuickAddOpen(booking);
    // Se in modalità edit precompila l'importo esistente per facilitare la correzione
    setQuickAmount(isEdit && booking.accounting_amount != null ? String(booking.accounting_amount) : '');
    setQuickCityTax('0');
    setQuickTaxRate(21);
  };

  const isManualBooking = (b) => !!b && (b.source === 'manual' || b.platform === 'manuale' || b.platform === 'direct');

  const deleteAccountingForBooking = async () => {
    if (!quickAddOpen || !quickAddOpen.linked_accounting_id) return;
    if (!window.confirm('Eliminare la voce di contabilità collegata a questa prenotazione?')) return;
    setQuickSaving(true);
    try {
      await axios.delete(`${API}/accounting/bookings/${quickAddOpen.linked_accounting_id}`, { withCredentials: true });
      toast.success('Voce eliminata dalla contabilità');
      setQuickAddOpen(null);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setQuickSaving(false);
    }
  };

  const saveQuickAccounting = async () => {
    if (!quickAddOpen) return;
    const amount = Number(String(quickAmount).replace(',', '.'));
    if (!amount || amount <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    const manual = isManualBooking(quickAddOpen);
    setQuickSaving(true);
    try {
      // Se stiamo modificando un record esistente, prima elimina il vecchio
      if (quickAddOpen.linked_accounting_id) {
        try {
          await axios.delete(`${API}/accounting/bookings/${quickAddOpen.linked_accounting_id}`, { withCredentials: true });
        } catch (e) {
          // ignora 404 (già eliminato)
        }
      }
      const payload = {
        ical_booking_id: quickAddOpen.id,
        gross_amount: amount,
        city_tax: Number(String(quickCityTax).replace(',', '.')) || 0,
        amount_type: manual ? 'gross' : 'net',
        tax_rate: manual ? Number(quickTaxRate) : 0,
        source: quickAddOpen.source === 'manual' ? 'manual' : 'ical',
      };
      const r = await axios.post(`${API}/accounting/bookings/quick-from-ical`, payload, { withCredentials: true });
      if (manual) {
        toast.success(`Registrato: €${amount.toFixed(2)} lordi · cedolare ${payload.tax_rate}% → €${(r.data.net || 0).toFixed(2)} netti`);
      } else {
        toast.success(`Bonifico €${amount.toFixed(2)} registrato in contabilità`);
      }
      setQuickAddOpen(null);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setQuickSaving(false);
    }
  };

  // Group bookings by relevant date (yesterday/today/tomorrow + week + upcoming)
  const buckets = useMemo(() => {
    if (!today) return null;
    const addDays = (n) => {
      const d = new Date(today + 'T00:00:00');
      d.setDate(d.getDate() + n);
      return d.toISOString().slice(0, 10);
    };
    const yesterday = addDays(-1);
    const tomorrow = addDays(1);

    const yesterdayCheckin = bookings.filter((b) => b.check_in === yesterday && !b.dismissed);
    const yesterdayCheckout = bookings.filter((b) => b.check_out === yesterday && !b.dismissed);
    const todayCheckin = bookings.filter((b) => b.check_in === today && !b.dismissed);
    const todayCheckout = bookings.filter((b) => b.check_out === today && !b.dismissed);
    const tomorrowCheckin = bookings.filter((b) => b.check_in === tomorrow && !b.dismissed);
    const tomorrowCheckout = bookings.filter((b) => b.check_out === tomorrow && !b.dismissed);

    // Week view: from day +2 to day +7 (oggi e domani sono già nel banner principale)
    const week = [];
    for (let i = 2; i < 7; i++) {
      const iso = addDays(i);
      const ins = bookings.filter((b) => b.check_in === iso && !b.dismissed);
      const outs = bookings.filter((b) => b.check_out === iso && !b.dismissed);
      if (ins.length || outs.length) week.push({ date: iso, ins, outs });
    }

    // Upcoming bookings: from day +7 onwards, grouped by month for readability
    const weekEnd = addDays(7);
    const upcoming = bookings.filter(
      (b) => !b.dismissed && b.check_in >= weekEnd,
    );
    const upcomingByMonth = {};
    upcoming.forEach((b) => {
      const k = (b.check_in || '').slice(0, 7);
      if (!upcomingByMonth[k]) upcomingByMonth[k] = [];
      upcomingByMonth[k].push(b);
    });

    const missingContracts = bookings.filter(
      (b) => !b.has_contract && !b.dismissed && b.days_to_checkin >= 0 && b.days_to_checkin <= 14,
    ).length;
    return { yesterdayCheckin, yesterdayCheckout, todayCheckin, todayCheckout, tomorrowCheckin, tomorrowCheckout, week, upcomingByMonth, upcomingCount: upcoming.length, missingContracts };
  }, [bookings, today]);

  if (loading || !today || !buckets) return null;

  // Empty state — nothing in the next 7 days
  const isEmpty = buckets.week.length === 0 && buckets.todayCheckin.length === 0 && buckets.todayCheckout.length === 0;
  const hasUpcoming = buckets.upcomingCount > 0;

  return (
    <div className="mb-6 sm:mb-8" data-testid="ical-calendar-section">
      {/* Header bar — same typography as DeadlinesCalendar/BigCounters */}
      <div className="flex items-end justify-between mb-3 sm:mb-5 gap-3">
        <div>
          <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-emerald-600 mb-1.5">
            Calendario prenotazioni
          </p>
          <h2 className="text-base sm:text-lg font-black tracking-tight text-zinc-900 flex items-center gap-2 leading-tight">
            <CalendarBlank size={20} weight="bold" className="text-zinc-900" />
            Prossimi arrivi e partenze
            {buckets.missingContracts > 0 && (
              <span className="inline-flex items-center gap-1 ml-1 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5">
                {buckets.missingContracts} {buckets.missingContracts === 1 ? 'contratto' : 'contratti'} mancanti
              </span>
            )}
          </h2>
          <p className="text-[11px] text-zinc-500 mt-1 leading-snug">
            Sincronizzato con Airbnb / Booking ogni 6 ore
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <button
            onClick={() => setManualOpen(true)}
            className="text-[10px] font-black uppercase tracking-wider text-white bg-zinc-900 hover:bg-zinc-800 inline-flex items-center gap-1.5 px-3 py-1.5 transition-colors"
            data-testid="ical-add-manual-btn"
          >
            <Plus size={12} weight="bold" />
            Aggiungi manuale
          </button>
          <button
            onClick={handleSyncNow}
            disabled={syncing}
            className="text-[10px] font-black uppercase tracking-wider text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1.5 border border-zinc-300 hover:border-zinc-900 px-2.5 py-1.5 transition-colors disabled:opacity-50"
            data-testid="ical-sync-now-btn"
          >
            <ArrowsClockwise size={12} weight="bold" className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Sync...' : 'Sync ora'}
          </button>
        </div>
      </div>

      {/* Yesterday + Today + Tomorrow urgent banner — sempre visibile, compatto */}
      {(buckets.yesterdayCheckin.length > 0 || buckets.yesterdayCheckout.length > 0 ||
        buckets.todayCheckin.length > 0 || buckets.todayCheckout.length > 0 ||
        buckets.tomorrowCheckin.length > 0 || buckets.tomorrowCheckout.length > 0) && (
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 text-white p-5 sm:p-6 mb-3 sm:mb-5" data-testid="today-banner">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* IERI */}
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400 mb-2">Ieri</p>
              {buckets.yesterdayCheckin.length === 0 && buckets.yesterdayCheckout.length === 0 ? (
                <p className="text-sm text-zinc-500">Nessun movimento.</p>
              ) : (
                <div className="space-y-2">
                  {buckets.yesterdayCheckin.map((b) => (
                    <BookingLine key={b.id} b={b} type="checkin" onCreateContract={handleCreateContract} onDismiss={handleDismiss} onRename={openRename} />
                  ))}
                  {buckets.yesterdayCheckout.map((b) => (
                    <BookingLine key={b.id} b={b} type="checkout" onCreateContract={handleCreateContract} onDismiss={handleDismiss} onRename={openRename} />
                  ))}
                </div>
              )}
            </div>
            {/* OGGI */}
            <div className="md:border-l md:border-zinc-700 md:pl-4">
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-2">Oggi</p>
              {buckets.todayCheckin.length === 0 && buckets.todayCheckout.length === 0 ? (
                <p className="text-sm text-zinc-400">Nessun movimento.</p>
              ) : (
                <div className="space-y-2">
                  {buckets.todayCheckin.map((b) => (
                    <BookingLine key={b.id} b={b} type="checkin" onCreateContract={handleCreateContract} onDismiss={handleDismiss} onRename={openRename} />
                  ))}
                  {buckets.todayCheckout.map((b) => (
                    <BookingLine key={b.id} b={b} type="checkout" onCreateContract={handleCreateContract} onDismiss={handleDismiss} onRename={openRename} />
                  ))}
                </div>
              )}
            </div>
            {/* DOMANI */}
            <div className="md:border-l md:border-zinc-700 md:pl-4">
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-2">Domani</p>
              {buckets.tomorrowCheckin.length === 0 && buckets.tomorrowCheckout.length === 0 ? (
                <p className="text-sm text-zinc-400">Nessun movimento.</p>
              ) : (
                <div className="space-y-2">
                  {buckets.tomorrowCheckin.map((b) => (
                    <BookingLine key={b.id} b={b} type="checkin" onCreateContract={handleCreateContract} onDismiss={handleDismiss} onRename={openRename} />
                  ))}
                  {buckets.tomorrowCheckout.map((b) => (
                    <BookingLine key={b.id} b={b} type="checkout" onCreateContract={handleCreateContract} onDismiss={handleDismiss} onRename={openRename} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Week view — list of upcoming days with bookings */}
      {isEmpty && !hasUpcoming ? (
        <div className="bg-white border-l-4 border-l-emerald-500 border-y border-r border-zinc-200 p-6 sm:p-8 text-center">
          <CalendarBlank size={36} className="text-emerald-300 mx-auto mb-3" />
          <p className="text-sm font-black text-zinc-900 mb-1 tracking-tight">
            {hasCalendars ? 'Nessuna prenotazione nei prossimi 6 mesi' : 'Nessun calendario sincronizzato'}
          </p>
          <p className="text-[11px] text-zinc-500 mb-4 leading-relaxed max-w-md mx-auto">
            {hasCalendars
              ? 'Il calendario è sincronizzato ma non ci sono prenotazioni in arrivo. Appena riceverai una prenotazione, comparirà qui automaticamente.'
              : 'Aggiungi il link iCal di Airbnb / Booking dalla sezione "I miei alloggi" e troverai qui ogni giorno chi arriva e chi parte.'}
          </p>
          {!hasCalendars && (
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={() => setManualOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-wider px-4 py-2.5 transition-colors"
                data-testid="ical-empty-manual-cta"
              >
                <Plus size={12} weight="bold" /> Aggiungi manualmente
              </button>
              <button
                onClick={() => navigate('/properties')}
                className="inline-flex items-center justify-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 text-[10px] font-black uppercase tracking-wider px-4 py-2.5 transition-colors"
                data-testid="ical-empty-cta"
              >
                Sincronizza iCal →
              </button>
            </div>
          )}
          {hasCalendars && (
            <button
              onClick={() => setManualOpen(true)}
              className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-wider px-4 py-2.5 transition-colors"
              data-testid="ical-empty-manual-cta"
            >
              <Plus size={12} weight="bold" /> Aggiungi prenotazione manualmente
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Toggle "Mostra altre prenotazioni" — default collapsed to keep dashboard compact su mobile */}
          {(!isEmpty || hasUpcoming) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="w-full flex items-center justify-between bg-white border border-zinc-200 hover:border-zinc-900 px-4 sm:px-5 py-3 transition-colors group"
              data-testid="toggle-upcoming-list"
            >
              <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-zinc-900">
                <CalendarBlank size={14} weight="bold" />
                {expanded ? 'Nascondi prossime prenotazioni' : 'Mostra altre prenotazioni'}
              </span>
              <span className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-zinc-500 tabular-nums">
                  {buckets.week.reduce((n, w) => n + w.ins.length + w.outs.length, 0) + buckets.upcomingCount} totali
                </span>
                <ArrowRight size={12} weight="bold" className={`text-zinc-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
              </span>
            </button>
          )}

          {expanded && !isEmpty && (
            <div className="bg-white border-l-4 border-l-emerald-500 border-y border-r border-zinc-200 divide-y divide-zinc-100 mt-2">
              {buckets.week.map(({ date: iso, ins, outs }) => (
                <div key={iso} className="px-5 sm:px-6 py-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-700 mb-3">
                    {fmtRelative(iso, today)} · {fmtDayOfWeek(iso)}
                  </p>
                  <div className="space-y-2">
                    {ins.map((b) => (
                      <BookingCard key={'in-' + b.id} b={b} type="checkin" onCreateContract={handleCreateContract} onCreateReceipt={handleCreateReceipt} onQuickAccounting={openQuickAdd} onDismiss={handleDismiss} onRename={openRename} />
                    ))}
                    {outs.map((b) => (
                      <BookingCard key={'out-' + b.id} b={b} type="checkout" onCreateContract={handleCreateContract} onCreateReceipt={handleCreateReceipt} onQuickAccounting={openQuickAdd} onDismiss={handleDismiss} onRename={openRename} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upcoming bookings grouped by month — paginated 10 at a time */}
          {expanded && hasUpcoming && (() => {
            // Flatten upcoming bookings preserving month grouping
            const allMonths = Object.entries(buckets.upcomingByMonth).sort(([a], [b]) => a.localeCompare(b));
            const flat = [];
            allMonths.forEach(([month, items]) => items.forEach((b) => flat.push({ month, b })));
            const visible = flat.slice(0, upcomingLimit);
            // Re-group visible by month for rendering
            const visibleByMonth = {};
            visible.forEach(({ month, b }) => {
              if (!visibleByMonth[month]) visibleByMonth[month] = [];
              visibleByMonth[month].push(b);
            });
            const remaining = flat.length - visible.length;
            return (
              <div className="mt-4 bg-white border-l-4 border-l-zinc-300 border-y border-r border-zinc-200" data-testid="ical-upcoming">
                <div className="px-5 sm:px-6 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-700">
                    Prenotazioni future · {buckets.upcomingCount}
                  </p>
                  <p className="text-[10px] text-zinc-500">
                    {visible.length} di {flat.length} · oltre 7 giorni
                  </p>
                </div>
                {Object.entries(visibleByMonth).sort(([a], [b]) => a.localeCompare(b)).map(([month, items]) => (
                  <div key={month} className="px-5 sm:px-6 py-4 border-b last:border-b-0 border-zinc-100">
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400 mb-3">
                      {new Date(month + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }).toUpperCase()} · {items.length}
                    </p>
                    <div className="space-y-2">
                      {items.map((b) => (
                        <BookingCard key={'u-' + b.id} b={b} type="checkin" onCreateContract={handleCreateContract} onCreateReceipt={handleCreateReceipt} onQuickAccounting={openQuickAdd} onDismiss={handleDismiss} onRename={openRename} />
                      ))}
                    </div>
                  </div>
                ))}
                {/* Load-more / collapse controls */}
                {flat.length > 10 && (
                  <div className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-zinc-50 flex flex-col sm:flex-row items-center justify-center gap-2">
                    {upcomingLimit < flat.length && (
                      <button
                        type="button"
                        onClick={() => setUpcomingLimit((n) => Math.min(n + 10, flat.length))}
                        className="bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-black uppercase tracking-wider px-5 py-2.5 transition-colors flex items-center gap-1.5"
                        data-testid="upcoming-load-more"
                      >
                        Mostra altre {Math.min(10, flat.length - upcomingLimit)}
                        <ArrowRight size={11} weight="bold" />
                      </button>
                    )}
                    {upcomingLimit > 10 && (
                      <button
                        type="button"
                        onClick={() => setUpcomingLimit(10)}
                        className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 px-3 py-2"
                        data-testid="upcoming-collapse"
                      >
                        Riduci elenco
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}

      {/* Quick Accounting Modal */}
      {quickAddOpen && (() => {
        const manual = isManualBooking(quickAddOpen);
        const platformLabel = (PLATFORM_STYLE[quickAddOpen.platform] || PLATFORM_STYLE.direct).label;
        const editing = !!quickAddOpen.linked_accounting_id;
        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !quickSaving && setQuickAddOpen(null)} data-testid="quick-accounting-modal">
          <div className="bg-white max-w-md w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-emerald-700 mb-1">
                  {editing ? 'Modifica contabilità' : 'Aggiungi a contabilità'}
                </p>
                {manual ? (
                  <>
                    <p className="text-sm font-black text-zinc-900 tracking-tight">Quanto ha pagato l'ospite?</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">Prenotazione manuale · ti aiutiamo a calcolare cedolare 21% (prima casa) o 26% (dal 2° immobile)</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-black text-zinc-900 tracking-tight">Quanto ti ha bonificato {platformLabel}?</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">Importo netto già al netto di commissioni e cedolare trattenuta dalla piattaforma</p>
                  </>
                )}
              </div>
              <button onClick={() => !quickSaving && setQuickAddOpen(null)} className="text-zinc-400 hover:text-zinc-700" data-testid="quick-accounting-close">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">
                  {manual ? 'Importo lordo (€)' : `Bonifico netto (€)`}
                </label>
                <input type="number" step="0.01" min="0" autoFocus value={quickAmount} onChange={(e) => setQuickAmount(e.target.value)} placeholder={manual ? '350.00' : '280.00'} className="w-full h-11 px-3 border border-zinc-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none text-base font-black tabular-nums" data-testid="quick-accounting-amount" />
              </div>
              {manual && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Cedolare secca</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setQuickTaxRate(21)}
                      className={`h-10 text-xs font-black uppercase tracking-wider transition-colors ${quickTaxRate === 21 ? 'bg-emerald-600 text-white' : 'bg-white border border-zinc-300 text-zinc-700 hover:border-zinc-900'}`}
                      data-testid="quick-tax-21"
                    >
                      21% · Prima casa
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuickTaxRate(26)}
                      className={`h-10 text-xs font-black uppercase tracking-wider transition-colors ${quickTaxRate === 26 ? 'bg-emerald-600 text-white' : 'bg-white border border-zinc-300 text-zinc-700 hover:border-zinc-900'}`}
                      data-testid="quick-tax-26"
                    >
                      26% · Dal 2° immobile
                    </button>
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Tassa di soggiorno (€)</label>
                <input type="number" step="0.01" min="0" value={quickCityTax} onChange={(e) => setQuickCityTax(e.target.value)} placeholder="0.00" className="w-full h-10 px-3 border border-zinc-300 text-sm tabular-nums" data-testid="quick-accounting-citytax" />
              </div>
              {manual && quickAmount && (
                <div className="bg-emerald-50 border border-emerald-200 p-2.5 text-[11px] text-emerald-900">
                  <p className="font-bold mb-0.5">Anteprima calcolo</p>
                  {(() => {
                    const g = Number(String(quickAmount).replace(',', '.')) || 0;
                    const ct = Number(String(quickCityTax).replace(',', '.')) || 0;
                    const taxable = Math.max(0, g - ct);
                    const tax = taxable * Number(quickTaxRate) / 100;
                    const net = g - ct - tax;
                    return (
                      <p className="tabular-nums">
                        Lordo €{g.toFixed(2)} − Tassa soggiorno €{ct.toFixed(2)} − Cedolare {quickTaxRate}% (€{tax.toFixed(2)}) = <strong>€{net.toFixed(2)} netti</strong>
                      </p>
                    );
                  })()}
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                {editing && (
                  <Button
                    onClick={deleteAccountingForBooking}
                    disabled={quickSaving}
                    variant="outline"
                    className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50 hover:border-rose-500 text-xs font-bold uppercase tracking-wider h-9 mr-auto"
                    data-testid="quick-accounting-delete"
                  >
                    Elimina
                  </Button>
                )}
                <Button onClick={() => setQuickAddOpen(null)} variant="outline" disabled={quickSaving} className="rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider h-9">Annulla</Button>
                <Button onClick={saveQuickAccounting} disabled={quickSaving || !quickAmount} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider h-9" data-testid="quick-accounting-save">
                  {quickSaving ? 'Salvo...' : (editing ? 'Aggiorna' : 'Aggiungi')}
                </Button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Confirm Delete (Blacklist) Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setConfirmDelete(null)} data-testid="confirm-delete-modal">
          <div className="bg-white max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 bg-rose-100 flex items-center justify-center flex-shrink-0">
                <Trash size={18} weight="bold" className="text-rose-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-black text-zinc-900 tracking-tight">Eliminare definitivamente?</p>
                <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                  Questa prenotazione verrà eliminata e <strong>non verrà più reimportata</strong> ai prossimi sync da {(PLATFORM_STYLE[confirmDelete.platform] || PLATFORM_STYLE.direct).label}.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <Button onClick={() => setConfirmDelete(null)} variant="outline" className="rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider h-9" data-testid="confirm-delete-cancel">Annulla</Button>
              <Button onClick={confirmDismiss} className="rounded-none bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-wider h-9" data-testid="confirm-delete-ok">
                Elimina e blacklist
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Guest Modal */}
      {renameTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !renameSaving && setRenameTarget(null)} data-testid="rename-guest-modal">
          <div className="bg-white max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] font-black uppercase tracking-wider text-emerald-700 mb-1">Nome ospite</p>
            <p className="text-sm font-black text-zinc-900 tracking-tight mb-3">Assegna o modifica il nominativo</p>
            <input
              autoFocus
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Mario Rossi"
              className="w-full h-11 px-3 border border-zinc-300 focus:border-emerald-600 outline-none text-sm"
              data-testid="rename-guest-input"
              onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); }}
            />
            <p className="text-[10px] text-zinc-400 mt-2">Booking spesso non condivide il nome. Inseriscilo qui e non verrà sovrascritto dai prossimi sync.</p>
            <div className="flex gap-2 justify-end mt-4">
              <Button onClick={() => setRenameTarget(null)} variant="outline" disabled={renameSaving} className="rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider h-9">Annulla</Button>
              <Button onClick={saveRename} disabled={renameSaving} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider h-9" data-testid="rename-guest-save">
                {renameSaving ? 'Salvo...' : 'Salva'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manual booking modal */}
      {manualOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !savingManual && setManualOpen(false)} data-testid="manual-booking-modal">
          <div className="bg-white max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-0.5">Calendario</p>
                <h3 className="text-sm font-black text-zinc-900 tracking-tight">Nuova prenotazione manuale</h3>
              </div>
              <button onClick={() => !savingManual && setManualOpen(false)} className="p-1 hover:bg-zinc-100" data-testid="manual-booking-close"><X size={18} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Struttura <span className="text-zinc-400 font-normal normal-case">(opzionale)</span></label>
                <select value={manualDraft.property_id} onChange={(e) => setManualDraft({ ...manualDraft, property_id: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 text-sm bg-white" data-testid="manual-booking-property">
                  <option value="">— Nessuna —</option>
                  {propertiesList.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.name} · {p.property_city}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Check-in</label>
                  <input type="date" value={manualDraft.check_in} onChange={(e) => setManualDraft({ ...manualDraft, check_in: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 text-sm" data-testid="manual-booking-checkin" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Check-out</label>
                  <input type="date" value={manualDraft.check_out} onChange={(e) => setManualDraft({ ...manualDraft, check_out: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 text-sm" data-testid="manual-booking-checkout" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Nome ospite</label>
                  <input type="text" value={manualDraft.guest_name} onChange={(e) => setManualDraft({ ...manualDraft, guest_name: e.target.value })} placeholder="Mario Rossi" className="w-full h-10 px-3 border border-zinc-300 text-sm" data-testid="manual-booking-guest" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">N° ospiti</label>
                  <input type="number" min="1" value={manualDraft.num_guests} onChange={(e) => setManualDraft({ ...manualDraft, num_guests: Number(e.target.value) || 1 })} className="w-full h-10 px-3 border border-zinc-300 text-sm tabular-nums" data-testid="manual-booking-numguests" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block mb-1.5">Note (opzionali)</label>
                <textarea rows={2} value={manualDraft.notes} onChange={(e) => setManualDraft({ ...manualDraft, notes: e.target.value })} className="w-full p-2 border border-zinc-300 text-sm resize-none" placeholder="Es. Cliente di fiducia, paga in contanti" />
              </div>
              <p className="text-[10px] text-zinc-400 leading-relaxed pt-1">
                ℹ️ Dopo aver salvato, troverai sulla prenotazione i tasti rapidi per generare contratto, ricevuta e registrarla in contabilità.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <Button onClick={() => setManualOpen(false)} variant="outline" disabled={savingManual} className="rounded-none text-xs font-bold uppercase tracking-wider h-9">Annulla</Button>
              <Button onClick={submitManual} disabled={savingManual} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider h-9" data-testid="manual-booking-save">
                {savingManual ? 'Salvo...' : 'Crea prenotazione'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact line used inside the dark Today/Tomorrow banner
function BookingLine({ b, type, onCreateContract, onDismiss, onRename }) {
  const platform = PLATFORM_STYLE[b.platform] || PLATFORM_STYLE.direct;
  const hasName = !!(b.guest_name && b.guest_name.trim());
  return (
    <div className="flex items-center justify-between gap-3" data-testid={`booking-line-${b.id}`}>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${platform.cls}`}>
          {platform.label}
        </span>
        <span className="text-xs font-bold text-emerald-300">
          {type === 'checkin' ? '↗ ARRIVA' : '↘ PARTE'}
        </span>
        <span className="text-sm text-white truncate">
          {b.property_name || b.summary || 'Prenotazione'}
          {hasName ? (
            <button
              onClick={() => onRename(b)}
              className="text-zinc-300 hover:text-white underline-offset-2 hover:underline ml-1"
              data-testid={`booking-rename-${b.id}`}
              title="Modifica nome ospite"
            >
              · {b.guest_name}
            </button>
          ) : (
            <button
              onClick={() => onRename(b)}
              className="ml-1 text-amber-300 italic hover:text-amber-200 underline-offset-2 hover:underline"
              data-testid={`booking-rename-${b.id}`}
              title="Assegna nome ospite"
            >
              · + nome ospite
            </button>
          )}
        </span>
      </div>
      {type === 'checkin' && !b.has_contract && (
        <button
          onClick={() => onCreateContract(b)}
          className="bg-amber-400 text-zinc-900 text-[10px] font-black uppercase tracking-wider px-2 py-1 hover:bg-amber-300 transition-colors flex items-center gap-1 flex-shrink-0"
          data-testid={`booking-contract-cta-${b.id}`}
        >
          <WarningCircle size={11} weight="fill" /> Crea contratto
        </button>
      )}
      {type === 'checkin' && b.has_contract && (
        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300 font-bold flex-shrink-0">
          <CheckCircle size={11} weight="fill" /> Contratto OK
        </span>
      )}
      <button
        onClick={() => onDismiss(b)}
        className="text-zinc-500 hover:text-rose-400 flex-shrink-0"
        title="Elimina e blacklist"
        data-testid={`booking-dismiss-${b.id}`}
      >
        <Trash size={13} weight="bold" />
      </button>
    </div>
  );
}

// Larger card used in the week list view
function BookingCard({ b, type, onCreateContract, onCreateReceipt, onQuickAccounting, onDismiss, onRename }) {
  const platform = PLATFORM_STYLE[b.platform] || PLATFORM_STYLE.direct;
  const hasName = !!(b.guest_name && b.guest_name.trim());
  return (
    <div className="border border-zinc-200 p-3 sm:p-3.5 hover:border-zinc-400 transition-colors" data-testid={`booking-card-${b.id}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 ${type === 'checkin' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'} flex items-center justify-center flex-shrink-0`}>
          {type === 'checkin' ? <Bed size={18} weight="bold" /> : <House size={18} weight="bold" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${platform.cls}`}>{platform.label}</span>
            <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">
              {type === 'checkin' ? 'ARRIVO' : 'PARTENZA'}
            </span>
            <span className="text-[10px] text-zinc-500 tabular-nums">
              {new Date(b.check_in + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
              {' → '}
              {new Date(b.check_out + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
              {' · '}{b.nights} {b.nights === 1 ? 'notte' : 'notti'}
            </span>
          </div>
          <p className="text-sm font-bold text-zinc-900 truncate">
            {b.property_name || b.summary || 'Prenotazione'}
          </p>
          <p className="text-xs text-zinc-500 truncate">
            {hasName ? (
              <button
                onClick={() => onRename(b)}
                className="hover:text-zinc-900 underline-offset-2 hover:underline"
                data-testid={`booking-card-rename-${b.id}`}
                title="Modifica nome ospite"
              >
                👤 {b.guest_name}
              </button>
            ) : (
              <button
                onClick={() => onRename(b)}
                className="italic text-amber-700 hover:text-amber-900 underline-offset-2 hover:underline inline-flex items-center gap-1"
                data-testid={`booking-card-rename-${b.id}`}
              >
                + Assegna nome ospite
              </button>
            )}
            {b.phone && <> · 📞 {b.phone}</>}
          </p>
        </div>
        <button
          onClick={() => onDismiss(b)}
          className="text-zinc-400 hover:text-rose-600 transition-colors p-1 flex-shrink-0"
          title="Elimina e blacklist"
          data-testid={`booking-card-dismiss-${b.id}`}
        >
          <Trash size={15} weight="bold" />
        </button>
      </div>
      {/* Action row */}
      <div className="flex items-center gap-1.5 flex-wrap pl-1">
        {b.has_contract ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1">
            <CheckCircle size={10} weight="fill" /> Contratto
          </span>
        ) : (
          <button
            onClick={() => onCreateContract(b)}
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-white bg-zinc-900 hover:bg-zinc-800 px-2.5 py-1.5 transition-colors"
            data-testid={`booking-card-contract-cta-${b.id}`}
          >
            Contratto <ArrowRight size={10} weight="bold" />
          </button>
        )}
        {b.has_receipt ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1">
            <CheckCircle size={10} weight="fill" /> Ricevuta
          </span>
        ) : (
          <button
            onClick={() => onCreateReceipt(b)}
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-700 border border-amber-400 hover:bg-amber-50 px-2.5 py-1.5 transition-colors"
            data-testid={`booking-card-receipt-cta-${b.id}`}
          >
            Ricevuta <ArrowRight size={10} weight="bold" />
          </button>
        )}
        {b.has_accounting ? (
          <button
            onClick={() => onQuickAccounting(b, true)}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-400 px-2 py-1 transition-colors"
            data-testid={`booking-card-accounting-edit-${b.id}`}
            title="Modifica o elimina importo"
          >
            <CheckCircle size={10} weight="fill" /> Contabilità {b.accounting_amount ? `€${Number(b.accounting_amount).toFixed(0)}` : ''} <span className="ml-1 text-zinc-400">✎</span>
          </button>
        ) : (
          <button
            onClick={() => onQuickAccounting(b)}
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-700 border border-emerald-600 hover:bg-emerald-50 px-2.5 py-1.5 transition-colors"
            data-testid={`booking-card-accounting-cta-${b.id}`}
          >
            + Contabilità
          </button>
        )}
      </div>
    </div>
  );
}
